/**
 * TEMP stub service for the TrustAI Executive Decision Board demo.
 *
 * There is no backend spec for this feature - it is a click-through demo
 * gated by the `isNewAgentOnboarding` tenant flag. This file declares the
 * API surface (types + method names) the mock registry resolves against.
 * Method names follow the generated convention (`list/get/...UsingGet`,
 * `...UsingPost`) because the mock layer resolves entries by `method.name`.
 *
 * TODO: delete this file if/when a real backend spec is generated for this
 * feature, then replace imports with the generated `@/api` service + DTOs.
 */

import type {
  EnforcementEnvironment,
  EnforcementReadinessCounts,
  EnforcementReadinessStatus,
  EnforcementLayer,
  EnforcementStatus,
} from "@/api/temp/AgentEstateService";

export type ControlSeverity = "critical" | "high" | "medium";

/** A prioritized control category card (section 3 - Highest-Priority Controls). */
export interface ControlCategory {
  id: string;
  name: string;
  severity: ControlSeverity;
  violationCount: number;
  affectedAgentCount: number;
  /**
   * The distinct agents behind `affectedAgentCount`, so a total across several
   * issues can be a union: an agent can sit under more than one issue.
   * Optional - a backend that returns counts only leaves it out.
   */
  affectedAgentIds?: string[];
  affectedApplicationCount: number;
  affectedBusinessUnits: string[];
  /** A short business statement shown directly on the executive dashboard. */
  businessImpact: string;
  /** Guardian's concise recommendation; supporting evidence remains in the drawer. */
  recommendation: string;
  /** Exactly one executive-facing reference — a standard or governance category. */
  reference: { label: "Standard" | "Category"; value: string };
  /** The access-policy principle this issue is measured against ("Data
   * Sovereignty", "Least Privilege", ...). Separate from `reference` so an
   * issue can cite a compliance standard *and* name its policy principle. */
  policyContext: string;
  /** Triage order on the dashboard, 1 = highest. Explicit rather than implied
   * by array position: the Requires Attention carousel slices this list into
   * pages, so "which three does an executive see first" is a stated property,
   * not an artifact of literal order. Deliberately independent of `severity` —
   * ranking weighs exposure, affected agents and business impact together, so
   * a `critical` issue can legitimately rank below a `high` one. */
  priorityRank: number;
  /** Every dashboard issue is reviewable through the shared decision workflow. */
  hasDecision: true;
  /**
   * Relationship evidence: the Agent -> MCP -> Data paths behind this issue.
   *
   * Present only where such a relationship actually exists, which is what
   * gates the "View Access Flow" action. Deliberately the evidence itself
   * rather than a `supportsAccessFlow` boolean - the caller needs the agent
   * ids anyway to scope the flow view, and a flag would let the action and
   * the data it opens drift apart.
   */
  flowContext?: { label: string; agentIds: string[] };
  /** Derived server-side from whether this control was the most recently
   * enforced one this demo run (never a stored/mutated field) -- see
   * TrustAIExecutiveStore.listControls(). Drives the card's "Enforced" badge
   * on the main page once the decision panel closes. */
  isEnforced?: boolean;
}

export type IdentityModel =
  | "dedicated"
  | "delegated"
  | "shared-service-principal"
  | "platform-managed";

/**
 * Where an agent sits against its *required policy coverage* — deliberately not
 * the agent's lifecycle state (New / Evaluating / Active). Shares the estate's
 * vocabulary (`AgentEstateService.AgentSecurityStatus`) on purpose, so one
 * agent reads the same on the board and in the agent list.
 *
 * "Secure" is scoped to the access policies TrustAI evaluates — it is not a
 * claim that the agent is free of every security threat, and every surface
 * rendering these states carries the tooltip that says so. An agent TrustAI
 * cannot yet fully evaluate is NOT_EVALUATED, never NOT_SECURE: the evidence
 * supports "we don't know yet", not "this is unsafe".
 */
export type PolicyPostureState = "SECURE" | "NOT_SECURE" | "NOT_EVALUATED";

/** Why TrustAI cannot yet make a complete policy-governance determination.
 * Per-agent, so the Not Evaluated drawer can say which onboarding step is
 * outstanding rather than just that one is. */
export type NotEvaluatedReason =
  | "NEWLY_DISCOVERED"
  | "IDENTITY_UNRESOLVED"
  | "DATA_MAPPING_INCOMPLETE"
  | "EVALUATION_PENDING";

/** A discovered AI agent (Agent Registry preview + Guardian evidence). */
export interface Agent {
  id: string;
  name: string;
  platform: string;
  identityModel: IdentityModel;
  dataAccess: string[];
  sourceRegion?: string;
  destinationRegion?: string;
  riskState: string;
  policyPostureState: PolicyPostureState;
  /** Set when `policyPostureState` is "SECURE" — the policy actively governing this agent. */
  governingPolicy?: string;
  /** Set when `policyPostureState` is "NOT_SECURE" — which required coverage is missing. */
  policyGapReason?: string;
  /** Set when `policyPostureState` is "NOT_EVALUATED" — the outstanding evaluation step. */
  notEvaluatedReason?: NotEvaluatedReason;
}

/** Guardian's explanation for why a control is violated by a specific agent. */
export interface GuardianEvidence {
  agentId: string;
  agentName: string;
  platform: string;
  identityModel: IdentityModel;
  explanation: string;
  affectedData: string;
  sourceRegion: string;
  destinationRegion: string;
  observedEvents: number;
  missingPurpose: boolean;
  approvedBasis: "Missing" | "Approved";
  safeguardEvidence: "Missing" | "Present";
  severity: ControlSeverity;
  controlGap: string;
}

/** A reusable enforcement policy pack recommended for a control. */
export interface PolicyPack {
  id: string;
  name: string;
  description: string;
  requirements: string[];
  defaultScope: string;
  estimatedAgentCoverage: number;
  estimatedViolationsBlocked: number;
  centralAuthorizationRequired: boolean;
  auditLoggingEnabled: boolean;
}

/**
 * The containment strategies an executive chooses between on the Strategy
 * stage. Stable ids, because the page layer keys icons off them and the demo
 * state persists a selection across stage navigation — a positional index
 * would reshuffle the moment the seed is reordered.
 */
export type ContainmentStrategyId =
  | "guardian-authorization"
  | "restrict-sensitive-data"
  | "restrict-data-sources"
  | "disable-tools"
  | "enterprise-policy-pack"
  | "block-agent-access"
  | "emergency-kill-switch"
  | "custom-policy";

/** How much the business feels a strategy. The axis an executive trades risk
 * reduction against, so it is ordered, not categorical. */
export type DisruptionLevel =
  | "low"
  | "low-medium"
  | "medium"
  | "high"
  | "critical";

export type ComplianceCoverage = "full" | "partial" | "none";

export type StrategyAlternativeGroup = "primary" | "additional";

/**
 * One way to contain a violation. `affectedAgentCount` is deliberately absent:
 * the count comes from the executive's own filtered scope (`scopedAgents`), so
 * a strategy card can never disagree with the approve button.
 */
export interface ContainmentStrategy {
  id: ContainmentStrategyId;
  /** e.g. "Route Through Guardian Authorization". */
  name: string;
  /** One-line executive framing of what this strategy does. */
  summary: string;
  /** Card bullets, e.g. "Lowest business disruption", "Audit trail enabled". */
  benefits: string[];
  /** Read-only detail chips (data sources, tool names) shown inside the card.
   * Display-only — narrowing scope stays the Scope stage's job. */
  options?: string[];
  isRecommended: boolean;
  riskReductionPercent: number;
  disruption: DisruptionLevel;
  complianceCoverage: ComplianceCoverage;
  confidencePercent: number;
  riskyActivitiesPrevented: number;
  /** Controls which progressive-disclosure group contains this alternative. */
  alternativeGroup?: StrategyAlternativeGroup;
  /** Visually and semantically distinguishes incident-only containment. */
  isEmergencyAction?: boolean;
  /** Leaves this workflow instead of enforcing from it. */
  isHandoff?: boolean;
}

/** Why Guardian recommends one strategy over the alternatives. Guardian
 * explains here; it never authors policy. */
export interface GuardianStrategyRationale {
  strategyId: ContainmentStrategyId;
  reasons: string[];
  confidencePercent: number;
  alternativesNote: string;
}

/** One selectable scope option in the Business Impact Preview. */
export interface ScopeOption {
  id: string;
  label: string;
  agentCount: number;
  applicationCount: number;
  businessUnitCount: number;
  /** Exact membership keeps preview, approval, graph state, and enforcement aligned. */
  agentIds?: string[];
}

export interface ImpactPreview {
  scopeOptions: ScopeOption[];
  defaultScopeId: string;
  recentRiskyActivitiesBlocked: number;
  futureRequestsRouted: boolean;
  completeDecisionLogging: boolean;
  noExpectedDowntime: boolean;
  before: string[];
  after: string[];
}

/** One step in the vertical TrustAI IBAC decision pipeline. */
export interface IbacPipelineStep {
  id: string;
  label: string;
  value: string;
}

/** Section 1 of the executive decision workflow — what happened, in business
 * language, with the accountable owner. */
export interface ExecutiveSummary {
  /** Business-language issue title, e.g. "GDPR Employee Data Transfer Risk". */
  headline: string;
  severity: ControlSeverity;
  /** One or two sentences an executive can read without security context. */
  description: string;
  businessOwner: string;
  businessOwnerRole: string;
}

export type ImpactDimensionId = "business" | "compliance" | "operational";

/** One quantified line inside an impact card. `value` is pre-formatted
 * (currency, percent, plain count) because only the seed knows the unit. */
export interface ImpactMetric {
  label: string;
  value: string;
}

export interface ImpactDimension {
  id: ImpactDimensionId;
  /** Plain-language answer to "why should I care" for this dimension. */
  headline: string;
  metrics: ImpactMetric[];
}

/** A key/value row inside the collapsed Technical Details disclosure — the
 * only place implementation vocabulary is allowed in this workflow. */
export interface TechnicalDetail {
  label: string;
  value: string;
}

/**
 * One agent inside the affected scope, described by the *business* attributes
 * the executive filters operate on. Filters, scope chips, and the approved
 * agent count are all derived from this one list, so a narrowed filter can
 * never disagree with the count on the approve button.
 */
export interface AffectedAgent {
  id: string;
  name: string;
  businessUnit: string;
  application: string;
  dataSource: string;
  classification: string;
  complianceFramework: string;
}

/** Section 7 — what the policy stops versus what keeps working. Stated as
 * outcomes, never as policy rules. */
export interface BehaviorExplanation {
  blocked: string[];
  allowed: string[];
}

/** One suggested Guardian prompt and its prepared executive-level answer.
 * Guardian explains the recommendation here; it never configures policy. */
export interface GuardianQuestion {
  id: string;
  question: string;
  answer: string;
}

/** One supporting fact behind the confidence score (identity source,
 * connected data sources, recent activity count, matching regulations). */
export interface EvidenceSignal {
  id: string;
  label: string;
  value: string;
}

/**
 * The executive-facing half of a control decision: everything the decision
 * workflow renders in business language. Kept beside (not merged into) the
 * existing technical fields so the IBAC/Guardian sections keep their own
 * vocabulary and neither side has to be rewritten to serve the other.
 */
export interface ExecutiveBriefing {
  summary: ExecutiveSummary;
  impactDimensions: ImpactDimension[];
  technicalDetails: TechnicalDetail[];
  /** Bullet reasons behind the recommendation (regulation, company policy,
   * data classification, observed runtime behavior). */
  rationale: string[];
  /** 0-100. Rendered as a meter beside the recommendation. */
  confidencePercent: number;
  affectedAgents: AffectedAgent[];
  behavior: BehaviorExplanation;
  guardianQuestions: GuardianQuestion[];
  evidenceSignals: EvidenceSignal[];
}

/** Everything the Recommendation, Guardian, Impact, and IBAC sections need for
 * one selected control - kept as a single bundle so those sections can never
 * desynchronize when the presenter switches controls. */
export interface ControlDecision {
  controlId: string;
  recommendedAction: string;
  whyNow: string;
  affectedAgentIds: string[];
  affectedApplications: string[];
  complianceImpact: string[];
  expectedOutcome: string[];
  confidenceLabel: string;
  guardianEvidence: GuardianEvidence;
  policyPack: PolicyPack;
  /** The containment strategies offered on the Strategy stage, one of which is
   * flagged `isRecommended`. */
  containmentStrategies: ContainmentStrategy[];
  guardianStrategyRationale: GuardianStrategyRationale;
  impact: ImpactPreview;
  pipelineSteps: IbacPipelineStep[];
  evaluationDimensions: string[];
  currentDecisionLabel: string;
  /** The same decision in the past tense, for the enforcement-progress step
   * ("Block cross-region copy" -> "Cross-region copy blocked"). Carried as its
   * own field rather than derived, so no code has to conjugate English. */
  blockedStepLabel: string;
  ongoingEnforcementLabel: string;
  evidenceOutcomeLabel: string;
  /** Business-language briefing driving the executive decision workflow. */
  executive: ExecutiveBriefing;
  /** Set on estate-wide decisions, which can also cover agents discovered after
   * approval. Per-control decisions are scoped to the agents already in
   * violation, so they offer no future-coverage choice. */
  supportsFutureAgentCoverage?: boolean;
}

export interface EstateGroup {
  /** Stable key the UI maps to an icon, tone, and click behaviour. Never
   * position-dependent, so reordering the seed can't reshuffle the styling. */
  id: string;
  label: string;
  value: number;
  /**
   * How many of these appeared since the previous evaluation. A count, not a
   * pre-formatted string, so the sign and the pluralisation stay with the
   * translation — the same treatment `WhatsChangedItem.delta` gets. Omitted
   * where discovery does not apply.
   */
  discoveredDelta?: number;
  /** Short supporting copy shown under the label — currently only populated for
   * the Current Risk row list (AI Estate/Data Estate render label-only stats). */
  description?: string;
  /**
   * Whether discovery has actually reached this metric yet.
   *
   * A `value` of 0 is ambiguous on its own: it means "we looked and found
   * none" once discovery has completed, and "we have not looked yet" before
   * then. Only the first reading is an estate count, so a tile carrying
   * NOT_DISCOVERED says "Not discovered yet" and offers no inventory to open.
   * Omitted in the discovered state, where `value` speaks for itself.
   */
  discoveryState?: "DISCOVERED" | "NOT_DISCOVERED";
  /**
   * `UNAVAILABLE` when the count could not be read (still loading, or failed).
   * `value` is then meaningless and the tile says "Not available" - a metric
   * nothing has read is never shown as a count of zero.
   */
  availability?: "UNAVAILABLE";
  /**
   * Where the count came from when it is not the tenant's own data:
   * `demo-snapshot` is the bundled pre-production snapshot, and the tile says so.
   */
  source?: "demo-snapshot";
}

/** The three display buckets under the posture bar. `unresolvedContext` folds
 * IDENTITY_UNRESOLVED and DATA_MAPPING_INCOMPLETE together — an executive reads
 * "we don't have enough context yet" as one thing; the drawer breaks it out. */
export type NotEvaluatedBucketId =
  | "newlyDiscovered"
  | "unresolvedContext"
  | "requirePolicyReview";

export interface NotEvaluatedBucket {
  id: NotEvaluatedBucketId;
  count: number;
}

/** Policy coverage across the discovered AI estate — the section's primary KPI.
 * The three states partition the agent population, so `totalAgents` and every
 * percentage are deliberately NOT stored: both are derived
 * (`secure + notSecure + notEvaluated`, `round(secure / total * 100)`) so
 * there is no second source of truth that can drift from the discovery counts.
 *
 * `notEvaluated` is also the cohort Guardian's top recommendation scopes to, so
 * the posture card's copy and that scope can never disagree about who is
 * affected. */
export interface PolicyPosture {
  secure: number;
  notSecure: number;
  notEvaluated: number;
  /** Always sums to `notEvaluated`. */
  notEvaluatedBreakdown: NotEvaluatedBucket[];
}

export interface EnterprisePosture {
  /**
   * The estate headline, as one flat row of metrics rather than two grouped
   * cards: the reader's question here is "what are we monitoring", and
   * splitting AI from data made them compare two lists before they could
   * answer it. Order is display order.
   */
  estate: EstateGroup[];
  policyPosture: PolicyPosture;
  lastAnalyzedAt: string;
  outcomeDelta?: OutcomeDelta;
  /** Representative connected data platform names, revealed on demand from
   * the Data Estate group ("reveals connected data systems"). */
  dataPlatforms: string[];
}

export interface EnforcementResult {
  controlId: string;
  scopeId: string;
  enforcedAgentCount: number;
  blockedEventCount: number;
  /** Echoes back the approved future-agent coverage, so the proof surfaces can
   * state what was actually approved rather than assuming the default. */
  coversFutureAgents: boolean;
}

export interface ProofPanel {
  policy: string;
  scope: string;
  enforcement: string;
  outcome: string;
  evidence: string;
  verificationStatus: "Pending" | "Verified" | "Simulated";
}

export interface VerifiedOutcomes {
  issuesRemediated: number;
  agentsRoutedThroughIbac: number;
  agentsBlockedCrossRegionCopy: number;
  agentsRestrictedEmbargoed: number;
  agentsProtectedByIntentAuth: number;
  percentEuActionsLogged: number;
  proofPanel: ProofPanel;
}

export type AuditDecision = "allow" | "deny";

export interface AuditEvidenceRecord {
  id: string;
  timestamp: string;
  agent: string;
  userIdentity: string;
  dataClass: string;
  sourceRegion: string;
  destinationRegion: string;
  declaredPurpose: string;
  matchedPolicy: string;
  decision: AuditDecision;
  enforcementPoint: string;
  evidenceStatus: "generated" | "pending";
}

export interface EnforcePolicyParams {
  controlId: string;
  scopeId: string;
  /** How many agents the approved filter selection resolved to. Sent
   * explicitly because business filters can narrow the scope past any
   * predefined `ScopeOption`, and the proof panel must report what was
   * actually enforced. */
  agentCount: number;
  /** Exact approved scope; required for contextual posture and graph updates. */
  agentIds: string[];
  /** Whether the approval also covers agents discovered after enforcement.
   * Only meaningful when the decision sets `supportsFutureAgentCoverage`. */
  includeFutureAgents: boolean;
}

export interface OutcomeDeltaMetric {
  id: string;
  label: string;
  before: string;
  after: string;
}

export interface OutcomeDelta {
  controlId: string;
  title: string;
  summary: string;
  metrics: OutcomeDeltaMetric[];
}

export type EstateMetricId =
  | "ai-agents"
  | "mcp-servers"
  | "tools"
  | "data-sources"
  | "tool-registries"
  | "data-platforms"
  | "enterprise-datasets"
  | "sensitive-datasets";

export type EstateAssetType = EstateMetricId | "identity-sources" | "gateways";

export type EstateEnvironment = "Production" | "Staging" | "Development";
export type EstatePolicyStatus =
  | "Protected"
  | "Needs attention"
  | "Not evaluated";

export interface EstateAsset {
  id: string;
  name: string;
  type: EstateAssetType;
  owner: string;
  businessUnit: string;
  environment: EstateEnvironment;
  status: EstatePolicyStatus;
  platform: string;
  /**
   * Machine key behind `platform`, when the asset has one. Data-source assets
   * carry the lowercase `AccountTypes` service key so a row can link into the
   * Data Sources list filtered by type; `platform` itself is a display label.
   */
  platformId?: string;
  description: string;
  violationCount: number;
  runtimeActive: boolean;
}

export interface EstateInventoryParams {
  metricId: EstateMetricId;
}

export interface EstateInventory {
  metricId: EstateMetricId;
  total: number;
  items: EstateAsset[];
}

export interface GuardianRecommendation {
  id: string;
  controlId: string;
  priority: 1 | 2 | 3;
  title: string;
  why: string;
  /** The named access policy Guardian would apply — what "Review recommended
   * policy" opens. */
  recommendedPolicy: string;
  agentCount: number;
  dataPlatformCount: number;
  businessUnits: string[];
  riskReductionPercent: number;
  complianceIncreasePercent: number;
}

/** One signed movement since the previous evaluation. `id` is an i18n key
 * suffix, never a label: user-facing copy stays in en.json. */
export interface WhatsChangedItem {
  id: string;
  delta: number;
  tone: "warning" | "info" | "success" | "neutral";
}

export interface WhatsChanged {
  items: WhatsChangedItem[];
}

export type RelationshipClusterBy =
  | "businessUnit"
  | "owner"
  | "environment"
  | "none";

export interface RelationshipNode {
  id: string;
  assetId: string;
  label: string;
  layer: "identity" | "agent" | "gateway" | "tool" | "data";
  assetType: EstateAssetType;
  owner: string;
  businessUnit: string;
  environment: EstateEnvironment;
  policyStatus: EstatePolicyStatus;
  violationCount: number;
  runtimeActive: boolean;
  description: string;
}

export interface RelationshipEdge {
  id: string;
  source: string;
  target: string;
  runtimeActive: boolean;
  policyStatus: EstatePolicyStatus;
  violationCount: number;
}

export interface RelationshipGraph {
  nodes: RelationshipNode[];
  edges: RelationshipEdge[];
  totals: Record<EstateAssetType, number>;
}

export type IncidentExposureStatus = "Gap" | "Partial" | "Ready" | "Verified";

/** One governance gap the external incident maps onto, expressed in our own
 * estate — a Guardian-style finding, not a claim the incident touched us. */
export interface IncidentExposure {
  id: string;
  label: string;
  count: number;
  summary: string;
  status: IncidentExposureStatus;
  /** Status/summary shown once the board reaches the "verified" enforcement
   * phase — the before/after payoff of approving and enforcing a policy. */
  appliedStatus: IncidentExposureStatus;
  appliedSummary: string;
}

/** External incident advisory (Section 2.5 — Incident Advisory). Frames the
 * board's own governance gaps against a real-world external incident to
 * motivate urgency, without claiming the incident itself touched this estate. */
export interface IncidentAdvisory {
  id: string;
  title: string;
  shortTitle: string;
  dateLabel: string;
  relevance: string;
  version: string;
  sourceLabel: string;
  sourceUrl: string;
  /** Tenant-neutral reassurance line, shown in both the banner and the drawer. */
  impactStatus: string;
  customerRelevance: string;
  matchingTechnologies: string[];
  relatedResources: string[];
  guardianInterpretation: string;
  whatHappenedHeadline: string;
  whatHappened: string;
  facts: { value: string; label: string; detail: string }[];
  signalsHeadline: string;
  signalsDisclaimer: string;
  signals: { id: string; title: string; detail: string }[];
  exposuresDisclaimer: string;
  exposures: IncidentExposure[];
  recommendations: { phase: string; items: string[] }[];
}

/* ------------------------------------------------------------- guardian */

/**
 * What Guardian is currently reasoning about. One assistant, many subjects —
 * the kind decides which script answers, the key which subject within it.
 */
export type GuardianContextKind =
  | "estate"
  | "business-function"
  | "ai-platform"
  | "data-platform"
  | "agent"
  | "risk"
  | "recommendation"
  // Onboarding, before there is an estate to reason about. Scripts under this
  // kind answer "what should I connect" rather than "what did you find".
  | "setup";

/** One suggested question, scoped to a context. */
export interface GuardianPromptSuggestion {
  id: string;
  /** Shown on the chip and sent verbatim as the question. */
  prompt: string;
  displayOrder: number;
}

/**
 * One Guardian answer.
 *
 * The two actions here are both *navigational*: reviewing a recommendation
 * opens the decision workflow, and viewing an access flow opens the flows view.
 * Neither changes policy. Guardian explains, investigates, compares and
 * recommends; enforcing is an explicit step the user takes elsewhere. Do not
 * add an "apply" field here.
 */
export interface GuardianScriptedReply {
  suggestions?: GuardianPromptSuggestion[];
  id: string;
  contextKey: string;
  /** Markdown, rendered through the shared assistant content renderer. */
  markdown: string;
  /**
   * The finding whose access flows this answer can open. Set only where that
   * finding carries relationship evidence, so Guardian never offers a view
   * that would open with nothing in it.
   */
  accessFlowFindingId?: string;
}

/* ------------------------------------------- governance landscape: data side */

export type DataGovernanceDimensionId =
  | "data-sources"
  | "monitoring-policies"
  | "access-controls";

/**
 * One stat inside a data-governance dimension.
 *
 * `attention` is the single flag that drives the warning marker and the
 * emphasised style. It is carried, never re-derived from the label: which state
 * counts as "needs attention" is a product judgement (Disabled does, Saved does
 * not) and belongs in the data, not in a string comparison in JSX.
 */
export interface DataGovernanceStat {
  /** i18n key suffix under `trustAiBoard.dataGovernance.stat`, never a label. */
  id: string;
  value: number | null;
  attention?: boolean;
}

export interface DataGovernanceDimension {
  id: DataGovernanceDimensionId;
  total: number | null;
  stats: DataGovernanceStat[];
}

/**
 * The three-part Data Estate summary. The standard preview sums platform
 * counts; snapshot mode uses supplied policy aggregates without inferring
 * a per-platform breakdown from them.
 */
export interface DataGovernanceOverview {
  dimensions: DataGovernanceDimension[];
}

/**
 * Every field is nullable because the live board cannot always know them.
 * `accountStats` is a tenant-wide aggregate with no per-platform split, so a
 * real platform carries its policy `total` (summed from its own data sources)
 * and withholds the state breakdown rather than inventing one. `null` renders
 * as "Not available"; `0` would be a claim that there are none.
 */
export interface MonitoringPolicyCounts {
  total: number | null;
  enabled: number | null;
  disabled: number | null;
}

/** Nullable for the same reason as `MonitoringPolicyCounts` above. */
export interface AccessControlCounts {
  total: number | null;
  deployed: number | null;
  saved: number | null;
  failed: number | null;
}

/** One data source on a platform that is not fully governed. */
export interface DataPlatformAttentionItem {
  id: string;
  name: string;
  /**
   * Any status `StatusBadge` can label, not a narrowed pair: the live board
   * passes the data source's own `accountStatus` straight through, so the
   * reader sees the same pill here as on the Data Sources table ("Missing
   * Info", "Failed Login"). The preview's own INCOMPLETE / NOT_EVALUATED are
   * renamed by the drawer's `labelOverrides`.
   */
  status: string;
  /** Pre-composed supporting lines, e.g. "5 failed access controls". */
  details: string[];
}

/**
 * One data platform's governance posture.
 *
 * Deliberately keeps the product's own vocabulary - Secured / Incomplete,
 * Enabled / Disabled, Deployed / Saved / Failed - rather than the cross-estate
 * "needs attention" aggregate. A reader who governs Snowflake thinks in failed
 * access controls, not in a generic protection score.
 */
export interface DataPlatformGovernance {
  snapshotSummary?: {
    active: number;
    monitoringTotal: number | null;
    accessTotal: number | null;
  };
  id: string;
  name: string;
  /** Key into `configs/services.ts` `getIcon()`; no new icon set. */
  serviceType: string;
  dataSourceTotal: number;
  securedCount: number;
  incompleteCount: number;
  monitoringPolicies: MonitoringPolicyCounts;
  accessControls: AccessControlCounts;
  attentionItems: DataPlatformAttentionItem[];
  /**
   * The inverse, Data -> AI direction: who reaches this platform.
   *
   * Optional: neither `accountStats` nor the account list knows agent reach, so
   * the live board omits both and the drawer drops the Agent Access section
   * entirely rather than reporting zero agents on a platform that has some.
   */
  accessingAgentCount?: number;
  providingMcpServerCount?: number;
}

/* -------------------------------------------------------- agent access flows */

/**
 * Governance state of one hop, or of one piece of evidence.
 *
 * Four states, not three: "not evaluated" is distinct from "no problem found",
 * and collapsing them would let an unexamined path read as a safe one.
 */
export type FlowGovernanceState =
  | "POLICY_ASSIGNED"
  | "POLICY_ENFORCED"
  | "POLICY_GAP"
  | "VIOLATION"
  | "NOT_EVALUATED";

export type FlowStatus = "NEEDS_ATTENTION" | "SECURE" | "NOT_EVALUATED";

export type FlowIdentityType = "SERVICE_ACCOUNT" | "MANAGED_IDENTITY";

export interface FlowNodeTarget {
  kind:
    | "identity"
    | "service"
    | "tool"
    | "resource"
    | "policy-gap"
    | "enforcement";
  /** For `enforcement`, the access path id (absent for the agent hop). */
  id?: string;
  layer?: EnforcementLayer;
  /** For `enforcement`, the Intent Policy whose enforcement to open. */
  policyId?: string;
}

/**
 * One control point an Intent Policy is enforced through, attached to the hop
 * it applies at. Access Flow answers where the agent can go; these say what
 * applies along the way.
 */
export interface FlowEnforcementIndicator {
  layer: EnforcementLayer;
  status: EnforcementStatus;
  /** The policy or controls in force at this hop. */
  control: string;
  policyId: string;
  pathId?: string;
}

export interface FlowNodeRef {
  enforcement?: FlowEnforcementIndicator[];
  gapIds?: string[];
  target?: FlowNodeTarget;
  state?: FlowGovernanceState;
  stateDetail?: string;
  label: string;
  /** The line above the label - the node's kind, owner or platform. */
  detail: string;
}

/**
 * One Agent -> MCP -> Platform -> Resource path.
 *
 * Governance state is attached to the hop it describes, split in two:
 * `accessState` covers agent-to-access-layer, `dataState` covers
 * access-layer-to-data. A single flow-level status could not say that the MCP
 * policy is missing while the data policy is enforced.
 */
export interface FlowBranch {
  id: string;
  accessLayer: FlowNodeRef;
  /** Ordered intermediaries; [] is direct access. Omitted preserves the legacy single hop. */
  accessLayers?: FlowNodeRef[];
  dataPlatform: FlowNodeRef;
  /** Drill-down inside the platform, e.g. DB -> SCHEMA -> TABLE. */
  resourcePath: string[];
  accessState: FlowGovernanceState;
  accessStateDetail: string;
  dataState: FlowGovernanceState;
  dataStateDetail: string;
}

/**
 * One governance finding about a flow.
 *
 * `controlId` points back at a `ControlCategory`, so "Review recommendation"
 * re-enters the existing decision workflow rather than opening a second policy
 * surface. Without both a `recommendedPolicy` and a `controlId` there is
 * nothing to review, and the action is not offered.
 */
export interface FlowEvidence {
  id: string;
  title: string;
  state: FlowGovernanceState;
  detail: string;
  resource?: string;
  recommendedPolicy?: string;
  controlId?: string;
  recommendationId?: string;
  activityId?: string;
  policyUrl?: string;
}

/**
 * One agent's access flow.
 *
 * Identity and agent are scalars; everything downstream is `branches`. The
 * model never assumes an agent has exactly one MCP server or one data source -
 * the interesting agents are precisely the ones that do not.
 */
export interface AgentAccessFlow {
  id: string;
  name: string;
  businessUnit: string;
  identity: string;
  identityType: FlowIdentityType;
  identityTarget?: FlowNodeTarget;
  status: FlowStatus;
  activePolicies: number;
  policyGaps: number;
  recentViolations: number;
  branches: FlowBranch[];
  evidence: FlowEvidence[];
  /** Intent Policies in effect at the agent itself. */
  agentEnforcement?: FlowEnforcementIndicator[];
  /**
   * TrustLogix enforcement readiness - a separate dimension from `status`.
   * Undefined when it couldn't be read, which reads as Status unknown.
   */
  enforcementStatus?: EnforcementReadinessStatus;
}

/**
 * The board's compact Agent Access Flows preview, and the flows view's filter
 * facets.
 *
 * The facets ride along here rather than being derived from a second, unfiltered
 * list request: the board already loads this summary, so by the time a reader
 * reaches the flows view the dropdown options are cache-warm, and no caller has
 * to pull the whole corpus just to learn which business units exist.
 */
export interface AgentFlowSummary {
  agentsNeedingAttention: number;
  serviceAccountsToReview: number;
  pathsWithPolicyGaps: number;
  businessUnits: string[];
  dataPlatforms: string[];
}

export type FlowStatusFilter = "attention" | "all" | "secure" | "not-evaluated";

export interface AgentAccessFlowListParams {
  businessUnit?: string;
  dataPlatform?: string;
  status?: FlowStatusFilter;
  /**
   * Comma-joined ids, scoping the list to one finding's affected agents.
   * A string rather than an array because `useQuery` builds its cache key from
   * `values(params)`, which must stay primitive.
   */
  agentIds?: string;
}

/* --------------------------------------------------------- estate readiness */

export type DiscoveryStatus = "NOT_STARTED" | "RUNNING" | "COMPLETED";
export type EvaluationStatus = "NOT_STARTED" | "RUNNING" | "COMPLETED";

/**
 * What TrustAI can currently see, and how far it has got with it.
 *
 * This is the dashboard's readiness signal, deliberately separate from
 * `EnterprisePosture`: posture makes claims *about* an estate, and those claims
 * are only meaningful once discovery and evaluation have run. Keeping the two
 * apart is what lets the page decline to show an analytic rather than show it
 * as zero — an unevaluated estate is not a clean one.
 *
 * The lifecycle state the UI renders from is derived, never stored, so there is
 * no second source of truth that can disagree with these counts. See
 * `getDashboardLifecycleState` in `@/pages/TrustAIExecutiveBoard/lifecycle`.
 */
/** Where the agents in the tenant's inventory came from. */
export type AgentInventorySourceKind =
  | "MANUAL"
  | "REGISTRY"
  | "MIXED"
  | "UNKNOWN";

/**
 * PROPOSED (no backend spec): the provenance of the persisted agent inventory.
 *
 * Absent means "unknown", never "manual" - a tenant with no connected registry
 * has not thereby been shown to have imported its agents by hand. Timestamps
 * are only ever the source's own: `lastImportedAt` for a manual import,
 * `lastSyncedAt` for a real registry synchronisation.
 */
export interface AgentInventorySource {
  source: AgentInventorySourceKind;
  /** Known size of the manually imported subset, when it is a subset. */
  manualAgentCount?: number;
  lastImportedAt?: string;
  lastSyncedAt?: string;
}

export interface EstateReadiness {
  /** PROPOSED: provenance of the agent inventory. Undefined when not supplied. */
  agentInventory?: AgentInventorySource;
  /**
   * TrustLogix enforcement readiness across the discovered agents. Undefined
   * when it could not be read - distinct from a zero count, and never shown as
   * one.
   */
  enforcement?: EnforcementReadinessCounts;
  /** The tenant's enforcement path; undefined when it could not be read. */
  enforcementEnvironment?: EnforcementEnvironment;
  connectedPlatforms: number;
  discoveredAgents: number;
  discoveredMcpServers: number;
  discoveredTools: number;
  discoveredDataSources: number;
  discoveryStatus: DiscoveryStatus;
  /** 0-100, already rounded. */
  discoveryPercentComplete: number;
  evaluationStatus: EvaluationStatus;
  /** 0-100, already rounded. */
  evaluationPercentComplete: number;
  /** Agents evaluation has reached so far. Equals `discoveredAgents` once COMPLETED. */
  agentsEvaluated: number;
  /** Data sources evaluation has analyzed so far. */
  dataSourcesAnalyzed: number;
  /**
   * Completed evaluation runs.
   *
   * "What's Changed" diffs this evaluation against the previous one, so it has
   * nothing to say until a second run exists — a first evaluation has no
   * baseline, and rendering the section anyway would invent a comparison.
   */
  completedEvaluations: number;
  /**
   * The estate a completed discovery will find - the denominator the
   * onboarding block counts towards. 0 before anything is connected, so a
   * "0 of 0" is never rendered as a claim about an estate.
   */
  totalAgents: number;
  /**
   * Progress across the whole onboarding run, discovery and evaluation
   * together, 0-100.
   *
   * The two phases are one run to the reader, so they get one bar. Blending the
   * two percentages (rather than switching from one to the other at the seam)
   * is what stops the number falling back to zero halfway through.
   */
  onboardingPercentComplete: number;
  /** Agents the run has analyzed so far, out of `totalAgents`. */
  agentsAnalyzed: number;
  /**
   * True while a run started from the onboarding wizard is in flight.
   *
   * Such a run continues into evaluation on its own, so the dashboard withholds
   * the manual "Start evaluation" prompt and keeps one progress block up until
   * the estate is fully populated.
   */
  chainedOnboarding: boolean;
}

/**
 * The lifecycle scenario a caller wants served, for the mock-only state
 * switcher. Passed as a query param rather than held in the store because
 * `useQuery` keys on `values(params)`: a scenario the store knew about but the
 * key did not would leave the cached entry untouched.
 */
export interface EstateReadinessParams {
  scenario?: string;
}

export interface ConnectPlatformParams {
  /** Catalog id of the integration being connected. */
  platformId: string;
}

const mockOnly = (): never => {
  throw new Error(
    "TrustAIExecutiveService is mock-only - calls are resolved via the mock registry inside <MockScope>.",
  );
};

/**
 * Mock-only service. Every method rejects when invoked against a real backend;
 * inside `<MockScope>` the `useQuery`/`useMutation` hooks resolve these by name
 * from `src/api/mock-data`.
 */
/*
 * IMPORTANT: declare these as regular static METHODS, not `static x = () => {}`
 * arrow-field initializers. The mock layer resolves entries by `method.name`,
 * and at this repo's `target: ES2020` (with `useDefineForClassFields` off) a
 * static arrow field compiles to `Svc.x = () => {}` - a member assignment whose
 * function `.name` is `""`, so the mock lookup silently misses. Method shorthand
 * keeps the correct `.name` at every target.
 */
export class TrustAIExecutiveService {
  static listAgentRegistryUsingGet(
    _params: Record<string, never> = {},
  ): Promise<Agent[]> {
    return Promise.resolve(mockOnly());
  }

  static listGuardianRecommendationsUsingGet(
    _params: Record<string, never> = {},
  ): Promise<GuardianRecommendation[]> {
    return Promise.resolve(mockOnly());
  }

  static getEstateInventoryUsingGet(
    _params: EstateInventoryParams,
  ): Promise<EstateInventory> {
    return Promise.resolve(mockOnly());
  }

  static getRelationshipGraphUsingGet(
    _params: Record<string, never> = {},
  ): Promise<RelationshipGraph> {
    return Promise.resolve(mockOnly());
  }

  static listGuardianPromptsUsingGet(_params: {
    contextKey: string;
    contextKind: GuardianContextKind;
    /** The subject's display name; the scripts substitute it. */
    label: string;
  }): Promise<GuardianPromptSuggestion[]> {
    return Promise.resolve(mockOnly());
  }

  static getGuardianContextUsingGet(_params: {
    contextKey: string;
    contextKind: GuardianContextKind;
    label: string;
    metadata?: Record<string, unknown>;
  }): Promise<GuardianScriptedReply> {
    return Promise.resolve(mockOnly());
  }

  static askGuardianUsingPost(_params: {
    contextKey: string;
    contextKind: GuardianContextKind;
    /** The subject's display name; the scripts substitute it. */
    label: string;
    metadata?: Record<string, unknown>;
    /** Null asks for the context's opening response. */
    promptId: string | null;
    /** Free-typed text, matched against the context's own prompts. */
    query: string;
  }): Promise<GuardianScriptedReply> {
    return Promise.resolve(mockOnly());
  }
  static getDataGovernanceOverviewUsingGet(
    _params: Record<string, never> = {},
  ): Promise<DataGovernanceOverview> {
    return Promise.resolve(mockOnly());
  }

  static listDataPlatformGovernanceUsingGet(
    _params: Record<string, never> = {},
  ): Promise<DataPlatformGovernance[]> {
    return Promise.resolve(mockOnly());
  }

  static getDataPlatformGovernanceUsingGet(_params: {
    platformId: string;
  }): Promise<DataPlatformGovernance | undefined> {
    return Promise.resolve(mockOnly());
  }

  static getAgentFlowSummaryUsingGet(
    _params: Record<string, never> = {},
  ): Promise<AgentFlowSummary> {
    return Promise.resolve(mockOnly());
  }

  static listAgentAccessFlowsUsingGet(
    _params: AgentAccessFlowListParams = {},
  ): Promise<AgentAccessFlow[]> {
    return Promise.resolve(mockOnly());
  }

  static getAgentAccessFlowUsingGet(_params: {
    agentId: string;
  }): Promise<AgentAccessFlow | undefined> {
    return Promise.resolve(mockOnly());
  }
}
